import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';

// Models
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import {
  TypeRankingsModel,
  buildSubTypeRankingsPath,
  buildSubTypeRankingsPathForAboveTheFold,
  buildSubTypeRankingsPathForBelowTheFold,
} from 'client/data/models/type-rankings';
import {
  getBestRankings,
  getBestRankingsForAboveTheFold,
  getBestRankingsForBelowTheFold,
  SEOModel,
} from 'client/data/models/seo';
import { DEFAULT_CONTENT, transform } from 'client/data/cms/content';
import { HomeModulesModel } from 'client/data/models/home-modules';

// Utils
import { TrackingHandler } from 'client/tracking/handler';
import { getBreadcrumbName, getUrlSlugByName } from 'site-modules/shared/utils/upper-funnel/type-mapping';
import { getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { scrollToHash } from 'client/utils/scroll';
import { getCustomSubtypeTitle } from 'client/site-modules/upper-funnel/utils/seo-content';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { getSubtypeDisplayTitle } from 'site-modules/upper-funnel/utils/subtype-display';
import {
  hasMostSoldTypeContent,
  mappedIndicesForSubtypeAdPlacement,
} from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { getSubtypeContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';

// Constants
import { TrackingMap } from 'client/tracking/maps/upper-funnel/type/type';

// Shared components
import { withCompareVehicleTray } from 'site-modules/shared/components/compare-vehicle-tray-wrapper/compare-vehicle-tray-wrapper';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { SuggestedVehiclePricingCarouselContainer } from 'site-modules/shared/components/suggested-vehicle-pricing-carousel/suggested-vehicle-pricing-carousel-container';
import { ExcoVideoViewer } from 'site-modules/shared/components/exco-video/exco-video-viewer';
import { VehicleShowcase } from 'site-modules/shared/components/upper-funnel/vehicle-showcase/vehicle-showcase';

// Components
import { TopRatedPromoUnit } from 'site-modules/shared/components/top-rated-promo-unit/top-rated-promo-unit';
import { VehicleTypeRankingsList } from 'site-modules/upper-funnel/components/type/vehicle-type-rankings-list/vehicle-type-rankings-list';
import { AnchorNavDropdown } from 'site-modules/upper-funnel/components/type/anchor-nav/anchor-nav-dropdown';
import { MostSoldTypeInfographic } from 'site-modules/shared/components/upper-funnel/most-sold-type-infographic/most-sold-type-infographic';
import { HeroContent } from 'site-modules/upper-funnel/components/type/hero-content/hero-content';

import { getSubtypeOverflowAdPos, getIndicesLength } from './get-subtype-overflow-ad-pos';

import './subtype.scss';

const VehicleTypeRankingsListWithCompareTray = withCompareVehicleTray(VehicleTypeRankingsList, {
  backFocusLink: {
    label: 'Skip to vehicle rankings',
    selector: '.vehicle-card .compare-checkbox input',
  },
});

function SubtypePageUI({
  content = DEFAULT_CONTENT,
  type,
  subtype,
  isMobile = false,
  referer = '',
  vehicleEditorialRankings = [],
  vehicleEditorialRankingsAboveTheFold = [],
  vehicleEditorialRankingsBelowTheFold = [],
  vehicleShowcaseContent = DEFAULT_CONTENT,
  location,
}) {
  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);

    scrollToHash(get(location, 'hash', ''), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRefererPathName = () => (referer ? get(referer.match(/edmunds.com(.*)/), '[1]', '') : '');
  const description = content.metadata('description').value();
  const breadCrumbData = [
    getHomeBreadcrumb(),
    {
      title: getBreadcrumbName(type),
      href: `/${getUrlSlugByName(type)}/`,
    },
    {
      title: subtype.replace(/suvs/g, 'SUVs'),
    },
  ];
  const trkParam = getRefererPathName(referer) === '/' ? { trk: type } : {};
  const title = getCustomSubtypeTitle(type, subtype);

  //   Most Sold Type Infographic
  const mostSoldTypeUniqueName = `${subtype}-${type}s`.toLowerCase();
  const mostSoldTypeUniqueNameSingular = mostSoldTypeUniqueName.substring(0, mostSoldTypeUniqueName.length - 1);
  const mostSoldTypeGraphTitle = hasMostSoldTypeContent(type, subtype)
    ? `Most Popular ${getSubtypeDisplayTitle(type, subtype)}`
    : null;

  const subheadTitle = getSubtypeDisplayTitle(type, subtype, true);

  const subtypeAdPlacementIndices = mappedIndicesForSubtypeAdPlacement(vehicleEditorialRankings);

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="type-page-v2 subtype-page-v2"
      role="main"
      aria-labelledby="type-page-heading"
    >
      <HeroContent
        breadcrumbsData={breadCrumbData}
        isMobile={isMobile}
        type={type}
        title={title}
        isChildPage
        subtypeTitle={subheadTitle}
      />
      <Container className="mb-2">
        <TopRatedPromoUnit className="my-2" isMobile={isMobile} />
        <div data-tracking-parent="edm-entry-summary" className="pt-sm-1_5 pb-1_25">
          <AnchorNavDropdown
            vehicleEditorialRankings={vehicleEditorialRankings}
            type={mostSoldTypeUniqueNameSingular}
            mostSoldTypeGraphTitle={mostSoldTypeGraphTitle}
            isMobile={isMobile}
            hasSeparator={false}
            containerClassName={null}
          />
        </div>
        <ExcoVideoViewer />
        <VehicleTypeRankingsListWithCompareTray
          type={type}
          trkParam={trkParam}
          vehicleEditorialRankings={[...vehicleEditorialRankingsAboveTheFold, ...vehicleEditorialRankingsBelowTheFold]}
          isSubtypePage
          subtypeAdPlacementIndices={subtypeAdPlacementIndices}
        />
        <div className="d-flex flex-column">
          {getSubtypeOverflowAdPos(getIndicesLength(subtypeAdPlacementIndices), trkParam, isMobile)}
        </div>
      </Container>
      <VehicleShowcase
        content={vehicleShowcaseContent}
        useContainer
        isMediumLayout={false}
        bottomBorder={false}
        className="mt-2 mb-3_5"
      />
      <Container>
        <div>
          {mostSoldTypeGraphTitle && (
            <ScrollElement id={`best-selling-${mostSoldTypeUniqueName}`}>
              <RenderWhenViewable
                verticalOffset="100%"
                placeholder={
                  <MostSoldTypeInfographic type={mostSoldTypeUniqueName} isMobile={isMobile} showMap={false} />
                }
              >
                <MostSoldTypeInfographic type={mostSoldTypeUniqueName} isMobile={isMobile} />
              </RenderWhenViewable>
            </ScrollElement>
          )}
          {description && <ContentFragment classes="text-gray-darker mb-3 description">{description}</ContentFragment>}
          <MinHeightWrapper
            className="suggested-pricing-wrapper mt-2"
            componentType="SUGGESTED_VEHICLE_PRICING_CAROUSEL"
          >
            <SuggestedVehiclePricingCarouselContainer />
          </MinHeightWrapper>
        </div>
      </Container>
    </main>
  );
}

SubtypePageUI.propTypes = {
  type: PropTypes.string.isRequired,
  subtype: PropTypes.string.isRequired,
  content: CmsEntities.Content,
  isMobile: PropTypes.bool,
  location: PropTypes.shape({}).isRequired,
  vehicleEditorialRankings: PropTypes.arrayOf(TypeRankingsEntities.VehicleRanking),
  vehicleEditorialRankingsAboveTheFold: PropTypes.arrayOf(TypeRankingsEntities.VehicleRanking),
  vehicleEditorialRankingsBelowTheFold: PropTypes.arrayOf(TypeRankingsEntities.VehicleRanking),
  vehicleShowcaseContent: CmsEntities.Content,
  referer: PropTypes.string,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  referer: get(state, 'pageContext.page.referer'),
  location: get(state, 'pageContext.location'),
});

const stateToPropsConfig = {
  content: bindToContent(({ type, subtype }) => getSubtypeContentPath(type, subtype)),
  vehicleShowcaseContent: bindToPath('vehicleShowcaseContent', HomeModulesModel, transform),
};

export const SubtypePage = connect(mapStateToProps)(
  connectToModel(SubtypePageUI, {
    ...stateToPropsConfig,
    vehicleEditorialRankings: bindToPath(
      ({ type, subtype }) => buildSubTypeRankingsPath(`${type},${subtype}`, subtype),
      TypeRankingsModel
    ),
    vehicleEditorialRankingsAboveTheFold: bindToPath(
      ({ type, subtype }) => buildSubTypeRankingsPathForAboveTheFold(`${type},${subtype}`, subtype),
      TypeRankingsModel
    ),
    vehicleEditorialRankingsBelowTheFold: bindToPath(
      ({ type, subtype }) => buildSubTypeRankingsPathForBelowTheFold(`${type},${subtype}`, subtype),
      TypeRankingsModel
    ),
  })
);
export const SubtypePageSeo = connect(mapStateToProps)(
  connectToModel(SubtypePageUI, {
    ...stateToPropsConfig,
    vehicleEditorialRankings: bindToPath(getBestRankings(), SEOModel),
    vehicleEditorialRankingsAboveTheFold: bindToPath(getBestRankingsForAboveTheFold(), SEOModel),
    vehicleEditorialRankingsBelowTheFold: bindToPath(getBestRankingsForBelowTheFold(), SEOModel),
  })
);
